import { apiGet, apiPut, apiPost } from './client';
import type { PlanTier } from '@/types';
import type { Block } from '@realsite/blocks';

export interface Template {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  preview_url: string | null;
  thumbnail_url: string | null;
  plan_required: PlanTier;
  locked: boolean;
  is_active: boolean;
  sort_order: number;
  is_applied: boolean;
}

export interface TemplateCustomizations {
  siteName: string;
  tagline?: string;
  location?: string;
  phone?: string;
  email?: string;
  address?: string;
  primaryColor?: string;
  accentColor?: string;
  logoUrl?: string;
}

export interface TemplatesResponse {
  templates: Template[];
}

export async function getTemplates(): Promise<Template[]> {
  const res = await apiGet<TemplatesResponse>('/templates');
  return res.templates;
}

/** A template's HOME-page blocks + style for a live in-wizard preview. For non-block
 *  templates `blocks` is null and `preview_url` (static image) is the fallback. */
export interface TemplatePreview {
  slug: string;
  name: string;
  format: string;
  blocks: Block[] | null;
  style?: Record<string, unknown> | null;
  preview_url?: string | null;
  pages?: Array<{ name: string; slug: string; is_home_page: boolean }>;
}

export async function getTemplatePreview(slug: string): Promise<TemplatePreview> {
  return apiGet<TemplatePreview>(`/templates/${slug}/preview`);
}

export async function applyTemplate(
  tenantId: string,
  templateId: number,
): Promise<{ template_id: number; template_slug: string; template_name: string }> {
  return apiPut(`/tenants/${tenantId}/template`, { template_id: templateId });
}

export interface ApplyTemplateResponse {
  template_slug: string;
  template_name: string;
  format?: string;
  /** True when the site already had content: the template is in the DRAFT, and the live site is
   *  unchanged until Publish (or Discard). False/absent: an empty site, built directly. */
  staged?: boolean;
  pages?: Array<{ id: string; name: string; slug: string; component_config: unknown; is_home_page: number; sort_order: number }>;
}

/**
 * Apply a template to the active site.
 *
 * `confirmReplace` must come from the agent saying yes to replacing their site's pages and look
 * (ConfirmTemplateDialog). It used to be sent as `true` on every call, which made the gateway's
 * "this site already has content" guard unreachable — one click on a template card replaced a
 * live site (Dawn, 2026-10-05). Without it a built site answers 409 and nothing changes.
 */
export async function applyTemplateBySlug(
  slug: string,
  customizations?: TemplateCustomizations,
  opts: { confirmReplace?: boolean } = {},
): Promise<ApplyTemplateResponse> {
  return apiPost(`/templates/${slug}/apply`, {
    site_name: customizations?.siteName,
    tagline: customizations?.tagline,
    location: customizations?.location,
    phone: customizations?.phone,
    email: customizations?.email,
    address: customizations?.address,
    primary_color: customizations?.primaryColor,
    accent_color: customizations?.accentColor,
    logo_url: customizations?.logoUrl,
    confirm_replace: opts.confirmReplace === true,
  });
}

/** A template waiting in the active site's draft (applied, not yet published). */
export interface PendingTemplate {
  template_slug: string;
  template_name: string;
  created_at: string | null;
  added_pages: number;
  /** 'start_over' when Start over is waiting (POST /site/start-over), not a template. */
  kind?: 'template' | 'start_over';
  /** Start over: the pages Publish removes. */
  removed_pages?: string[];
  /** Start over: the community pages Publish removes. */
  removed_communities?: string[];
}

export async function getPendingTemplate(): Promise<PendingTemplate | null> {
  const res = await apiGet<{ pending: PendingTemplate | null }>('/templates/pending');
  return res?.pending ?? null;
}

/** Throw away the waiting template: its new pages go and every draft it replaced comes back. */
export async function discardPendingTemplate(): Promise<{ discarded: boolean; template_name: string | null; kept_pages?: string[] }> {
  // `{}`, not no body: a JSON content type with an empty body is a 400 at the gateway (#768).
  return apiPost('/templates/pending/discard', {});
}

/** What Start over can wipe (gateway lib/site-start-over.ts START_OVER_PARTS). */
export type StartOverPart = 'pages' | 'menu' | 'look' | 'blog' | 'agents' | 'communities';

/** What the agent types to confirm Start over; the gateway checks it too. */
export const START_OVER_CONFIRM = 'start over';

/**
 * Start over: the active site's pages, menu and look go back to the starter, in the DRAFT (gateway
 * lib/site-start-over.ts). A restore point is saved first; Publish takes it live, Discard undoes it.
 */
export async function startOverSite(
  confirm: string,
  parts: StartOverPart[],
): Promise<{ staged: boolean; parts: StartOverPart[]; reset_pages: number; removed_pages: string[]; removed_communities: string[] }> {
  return apiPost('/site/start-over', { confirm, parts });
}
